/**
 * Chatbot widget CSS styles embedded as a string.
 * This approach avoids webpack module resolution issues with CSS imports in libraries.
 */
export const CHATBOT_STYLES = `
/* ==========================================================================
   Chatbot Widget Styles
   BEM naming convention: block__element--modifier
   Scoped via [data-chatbot-root] to avoid polluting :root
   ========================================================================== */

/* --------------------------------------------------------------------------
   CSS Custom Properties (Theming Tokens)
   -------------------------------------------------------------------------- */
[data-chatbot-root] {
  --chatbot-primary: #1a73e8;
  --chatbot-primary-hover: #1557b0;
  --chatbot-bg: #ffffff;
  --chatbot-bg-secondary: #f1f3f4;
  --chatbot-text: #202124;
  --chatbot-text-secondary: #5f6368;
  --chatbot-border: #dadce0;
  --chatbot-error: #d93025;
  --chatbot-user-msg-bg: #e8f0fe;
  --chatbot-assistant-msg-bg: #f1f3f4;
  --chatbot-font-family: system-ui, -apple-system, sans-serif;
  --chatbot-font-size: 14px;
  --chatbot-border-radius: 12px;
  --chatbot-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  --chatbot-z-index: 9999;
}

/* --------------------------------------------------------------------------
   Floating Action Button (.chatbot-fab)
   -------------------------------------------------------------------------- */
.chatbot-fab {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: var(--chatbot-z-index);
  width: 56px;
  height: 56px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 50%;
  background-color: var(--chatbot-primary);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: var(--chatbot-shadow);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.chatbot-fab:hover {
  background-color: var(--chatbot-primary-hover);
  transform: scale(1.05);
}

.chatbot-fab:focus-visible {
  outline: 2px solid var(--chatbot-primary);
  outline-offset: 2px;
  background-color: var(--chatbot-primary-hover);
}

.chatbot-fab:active {
  transform: scale(0.95);
}

.chatbot-fab__icon {
  width: 24px;
  height: 24px;
  transition: opacity 0.2s ease;
}

.chatbot-fab__icon--chat[hidden],
.chatbot-fab__icon--close[hidden] {
  display: none;
}

/* --------------------------------------------------------------------------
   Chat Panel (.chatbot-panel)
   -------------------------------------------------------------------------- */
.chatbot-panel {
  position: fixed;
  bottom: 80px;
  right: 16px;
  z-index: var(--chatbot-z-index);
  min-width: 320px;
  max-width: 400px;
  width: 400px;
  max-height: min(500px, 70vh);
  background-color: var(--chatbot-bg);
  border: 1px solid var(--chatbot-border);
  border-radius: var(--chatbot-border-radius);
  box-shadow: var(--chatbot-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: var(--chatbot-font-family);
  font-size: var(--chatbot-font-size);
  color: var(--chatbot-text);
}

.chatbot-panel[hidden] {
  display: none;
}

/* --------------------------------------------------------------------------
   Panel Header (.chatbot-panel__header)
   -------------------------------------------------------------------------- */
.chatbot-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--chatbot-border);
  background-color: var(--chatbot-bg);
  flex-shrink: 0;
}

.chatbot-panel__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--chatbot-text);
  line-height: 1.4;
}

/* --------------------------------------------------------------------------
   Close Button (.chatbot-panel__close)
   -------------------------------------------------------------------------- */
.chatbot-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--chatbot-text-secondary);
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.chatbot-panel__close:hover {
  background-color: var(--chatbot-bg-secondary);
  color: var(--chatbot-text);
}

.chatbot-panel__close:focus-visible {
  outline: 2px solid var(--chatbot-primary);
  outline-offset: 2px;
}

/* --------------------------------------------------------------------------
   Message Area (.chatbot-panel__messages)
   -------------------------------------------------------------------------- */
.chatbot-panel__messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

/* --------------------------------------------------------------------------
   Messages (.chatbot-panel__message)
   -------------------------------------------------------------------------- */
.chatbot-panel__message {
  display: flex;
  flex-direction: column;
  max-width: 85%;
  gap: 4px;
}

.chatbot-panel__message--user {
  align-self: flex-end;
  align-items: flex-end;
}

.chatbot-panel__message--assistant {
  align-self: flex-start;
  align-items: flex-start;
}

.chatbot-panel__message-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--chatbot-text-secondary);
  padding: 0 4px;
}

.chatbot-panel__message-content {
  padding: 8px 12px;
  border-radius: 8px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.chatbot-panel__message-content > :first-child {
  margin-top: 0;
}

.chatbot-panel__message-content > :last-child {
  margin-bottom: 0;
}

.chatbot-panel__message-content p,
.chatbot-panel__message-content ul,
.chatbot-panel__message-content ol,
.chatbot-panel__message-content pre,
.chatbot-panel__message-content h1,
.chatbot-panel__message-content h2,
.chatbot-panel__message-content h3,
.chatbot-panel__message-content h4,
.chatbot-panel__message-content h5,
.chatbot-panel__message-content h6 {
  margin: 0 0 8px;
}

.chatbot-panel__message-content ul,
.chatbot-panel__message-content ol {
  padding-left: 20px;
}

.chatbot-panel__message-content a {
  color: inherit;
  text-decoration: underline;
}

.chatbot-panel__message-content code {
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.95em;
}

.chatbot-panel__message-content pre {
  overflow-x: auto;
  padding: 8px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.06);
}

.chatbot-panel__message-content pre code {
  display: block;
  white-space: pre;
}

.chatbot-panel__message--user .chatbot-panel__message-content {
  background-color: var(--chatbot-user-msg-bg);
  color: var(--chatbot-text);
}

.chatbot-panel__message--assistant .chatbot-panel__message-content {
  background-color: var(--chatbot-assistant-msg-bg);
  color: var(--chatbot-text);
}

/* --------------------------------------------------------------------------
   Error Messages (.chatbot-panel__message--error)
   -------------------------------------------------------------------------- */
.chatbot-panel__message--error .chatbot-panel__message-content {
  color: #c5221f;
  background-color: #fce8e6;
}

/* --------------------------------------------------------------------------
   Loading Indicator (.chatbot-panel__loading)
   -------------------------------------------------------------------------- */
.chatbot-panel__loading {
  align-self: flex-start;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--chatbot-assistant-msg-bg);
  border-radius: 8px;
}

.chatbot-panel__loading-dots {
  display: inline-flex;
  gap: 4px;
  font-size: 0;
}

.chatbot-panel__loading-dots::before,
.chatbot-panel__loading-dots::after,
.chatbot-panel__loading-dots {
  content: '';
}

/* Animated dots using pseudo-elements and box-shadow */
.chatbot-panel__loading-dots {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--chatbot-text-secondary);
  animation: chatbot-dot-pulse 1.4s infinite ease-in-out both;
  animation-delay: 0.16s;
}

.chatbot-panel__loading-dots::before,
.chatbot-panel__loading-dots::after {
  position: absolute;
  top: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--chatbot-text-secondary);
  animation: chatbot-dot-pulse 1.4s infinite ease-in-out both;
}

.chatbot-panel__loading-dots::before {
  left: -12px;
  animation-delay: 0s;
}

.chatbot-panel__loading-dots::after {
  left: 12px;
  animation-delay: 0.32s;
}

@keyframes chatbot-dot-pulse {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* --------------------------------------------------------------------------
   Placeholder (.chatbot-panel__placeholder)
   -------------------------------------------------------------------------- */
.chatbot-panel__placeholder {
  text-align: center;
  color: var(--chatbot-text-secondary);
  padding: 24px 16px;
  font-style: italic;
  line-height: 1.5;
}

/* --------------------------------------------------------------------------
   Input Area (.chatbot-panel__input-area)
   -------------------------------------------------------------------------- */
.chatbot-panel__input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--chatbot-border);
  background-color: var(--chatbot-bg);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.chatbot-panel__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid var(--chatbot-border);
  border-radius: 8px;
  font-family: var(--chatbot-font-family);
  font-size: var(--chatbot-font-size);
  color: var(--chatbot-text);
  background-color: var(--chatbot-bg);
  resize: none;
  line-height: 1.5;
  transition: border-color 0.15s ease;
}

.chatbot-panel__input:focus {
  outline: none;
  border-color: var(--chatbot-primary);
  box-shadow: 0 0 0 1px var(--chatbot-primary);
}

.chatbot-panel__input:disabled {
  background-color: var(--chatbot-bg-secondary);
  color: var(--chatbot-text-secondary);
  cursor: not-allowed;
}

.chatbot-panel__input::placeholder {
  color: var(--chatbot-text-secondary);
}

/* --------------------------------------------------------------------------
   Character Counter (.chatbot-panel__char-count)
   -------------------------------------------------------------------------- */
.chatbot-panel__char-count {
  width: 100%;
  font-size: 12px;
  color: var(--chatbot-text-secondary);
  text-align: right;
  order: 3;
}

.chatbot-panel__char-count[hidden] {
  display: none;
}

/* --------------------------------------------------------------------------
   Send Button (.chatbot-panel__send)
   -------------------------------------------------------------------------- */
.chatbot-panel__send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 50%;
  background-color: var(--chatbot-primary);
  color: #ffffff;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.chatbot-panel__send:hover:not(:disabled) {
  background-color: var(--chatbot-primary-hover);
}

.chatbot-panel__send:focus-visible {
  outline: 2px solid var(--chatbot-primary);
  outline-offset: 2px;
}

.chatbot-panel__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chatbot-panel__send svg {
  width: 20px;
  height: 20px;
}

/* --------------------------------------------------------------------------
   SVG Styles
   -------------------------------------------------------------------------- */
.chatbot-fab svg,
.chatbot-panel__send svg {
  pointer-events: none;
  user-select: none;
}

/* --------------------------------------------------------------------------
   Responsive Design
   -------------------------------------------------------------------------- */
@media (max-width: 480px) {
  .chatbot-panel {
    min-width: 100vw;
    max-width: 100vw;
    width: 100vw;
    bottom: 0;
    right: 0;
    max-height: 100vh;
    border-radius: 16px 16px 0 0;
  }

  .chatbot-fab {
    bottom: 20px;
    right: 20px;
  }
}

/* --------------------------------------------------------------------------
   Reduced Motion
   -------------------------------------------------------------------------- */
@media (prefers-reduced-motion: reduce) {
  .chatbot-fab {
    transition: none;
  }

  .chatbot-fab__icon {
    transition: none;
  }

  .chatbot-panel__close {
    transition: none;
  }

  .chatbot-panel__input {
    transition: none;
  }

  .chatbot-panel__send {
    transition: none;
  }

  .chatbot-panel__loading-dots,
  .chatbot-panel__loading-dots::before,
  .chatbot-panel__loading-dots::after {
    animation: none;
    opacity: 1;
    transform: scale(1);
  }
}
`;
